'use client'

/**
 * The banner that marks feedback mode AND session recording (Issues #13715,
 * #13757).
 *
 * ## Why a banner at all
 *
 * Feedback mode mounts a `fixed inset-0` capture layer that swallows every
 * click on the page. That is the mechanism, not a side effect — but from the
 * viewer's side it is indistinguishable from the app having frozen. Demo mode
 * and impersonation both answer the same question ("why is this session not
 * behaving normally?") with an in-flow banner, and this is the third member of
 * that family rather than a new idea.
 *
 * Amber, because slate and purple are taken: demo mode is
 * `--color-bg-demo-mode-banner: var(--color-slate-700)` and impersonation is
 * `--color-status-purple-bg-emphasis`. The tokens are feature-private and live
 * beside demo mode's in `src/tokens/index.css`; they carry dark mode
 * themselves, so there is no `dark:` variant here and there must not be one.
 *
 * ## #13757 gave it a SECOND state, and folded the recorder panel into it
 *
 * #13580 shipped recording as a floating panel that existed only inside
 * feedback mode. Since feedback mode is also a full-viewport click
 * interceptor, there was no state in which a person could record themselves
 * USING the app: stay in the mode and you cannot navigate, leave it to
 * navigate and the recording stopped. The panel is gone and this banner
 * carries recording instead, in a red treatment, WHETHER OR NOT feedback mode
 * is on — which is what makes "record yourself doing something" a thing that
 * exists.
 *
 * So the render condition is deliberately not `isActive` any more. It is
 * "feedback mode, or a recording, or the confirmation for a recording that
 * just ended", and the three are independent. Each state's chrome comes from
 * its own feature-private token pair; `session-recording-context.tsx` owns
 * every piece of the machinery behind them and this file owns none of it.
 *
 * ## Where it is mounted, and why that is load bearing
 *
 * A sibling of `<FeedbackElementPicker />` inside `<FeedbackModeProvider>` in
 * `src/app/layout.tsx` — NOT in the header.
 *
 * #13640 deleted the `FEEDBACK_MODE_MIN_VIEWPORT` width floor, and what paid
 * for that deletion was "the overlay carries its own exit control at every
 * width and on every pointer". #13715 moves `Done` out of the overlay's
 * floating bar (three controls fit at 320px; four wrapped to two rows) and
 * into this banner, so the banner inherits that debt in full. Headerless
 * routes do not render the header at all, so a banner mounted there would
 * leave those routes as a `fixed inset-0` interceptor with no visible way out
 * — the exact failure the width floor existed to prevent.
 *
 * #13757 adds a second reason for the same mount: the root layout is the only
 * place that SURVIVES a client-side route change, and a recording indicator
 * that vanished on navigation would be a recording nobody can see running.
 *
 * ## Three properties that look cosmetic and are not
 *
 *  - IN FLOW and STICKY (`sticky top-0`, never `fixed`). It pushes the app
 *    down like its two siblings do, rather than covering whatever the route
 *    renders at the top — and since #14653 it stays pinned while the page
 *    scrolls, because `Done` and the recording Stop must never scroll out of
 *    reach while the capture layer swallows every click. Sticky alone would
 *    pin it at `top: 0` OVER the sticky app header, so it publishes its
 *    measured height as `--feedback-banner-height`
 *    (`use-feedback-banner-height.ts`) and every header shell pins at that
 *    offset via `APP_HEADER_STICKY_CLASS`. Portalling it INTO the header's
 *    sticky stack was rejected: that stack is `sticky … z-nav-secondary`, a
 *    stacking context, which would cap this banner at the nav layer — under
 *    the picker's capture layer, with controls that look clickable and are
 *    not.
 *  - `z-feedback-banner` (74; #16990 moved the band onto sai-ui's layer
 *    scale, it was `z-550`). The picker's capture layer is `fixed inset-0
 *    z-feedback-picker` with `pointer-events: auto`, so an in-flow banner at
 *    the default stacking level sits UNDER it and its controls become
 *    visible, focusable and unclickable — worse than absent, because they
 *    look like they work. The banner is above the picker and below
 *    `FeedbackModal`'s `z-feedback-modal`. It is ALSO above every ordinary
 *    `@/components/ui/modal`'s `z-modal` and every `z-menu` (#16841): it was
 *    once under the dialogs, so with a dialog open the mode's only exit sat
 *    under the backdrop. No portal is needed for that: every ancestor up to `<body>`
 *    in `src/app/layout.tsx` is a context provider that renders no element,
 *    so this sticky box competes in the root stacking context, the same one
 *    the modal's `document.body` portal lands in.
 *  - `data-feedback-mode-exempt`. The capture layer covers this banner, so
 *    without the opt-out the first thing a viewer picks after entering the
 *    mode is the banner telling them they are in it.
 *
 * ## The controls are made of the banner (#13757)
 *
 * #13715 gave `Done` a white `bg-surface-elevated` fill. On amber that reads as
 * a hole punched in the bar rather than a control on it, and it measured
 * 2.15:1 against the fill — the worst row in
 * `tests/unit/tokens/feedback-mode-control-contrast.test.ts`. Every control
 * here is now OUTLINED in the live state's own foreground token over a
 * low-alpha tint of that same token, which keeps #13715's 1.4.11 boundary and
 * makes the control belong to the bar in both states. The ratios are
 * arithmetic, not taste, and they are pinned in that file and in
 * `feedback-recording-banner-token.test.ts`.
 *
 * ## #13788 — the banner carries EVERYTHING, and the pins control leaves
 *
 * #13841 NOTE: the paragraph below describes the state after #13788 and is
 * kept because the CTA and the focus effect are still here for the reasons it
 * gives. Its last paragraph is superseded: pins follow the mode again, so the
 * show/hide control is not in the user menu either — it does not exist. See
 * `feedback-mode-context.tsx`. And the record control below is now the ONLY
 * entry point into a session recording, the user-menu row having gone with it.
 *
 * Two amber chromes existed for one mode: this banner and the picker's own
 * floating bar. On a fine pointer that bar held exactly one button, and it
 * stood down entirely while an anchor was pending — so the CTA vanished
 * precisely when a viewer might want it. The CTA and the focus-on-entry effect
 * both move up here; the bar survives only on the coarse two-step path,
 * carrying `Parent` / `Child`, because those two have to sit near the thumb
 * and near the highlight.
 *
 * The pins show/hide control that #13756 put here is GONE. #13788 moved it to
 * the user menu because pins had become ambient; #13841 deleted it outright
 * because they stopped being ambient again. Do not re-add one here without
 * re-reading `feedback-mode-context.tsx` — with pins drawn exactly when the
 * mode is live, a control on this banner would be a control for hiding the
 * thing the banner is up to say you are doing.
 *
 * Three control rows, one per state — `placing`, `browsing`, `recording`. The
 * ORDER inside `placing` is load bearing: see the focus effect.
 */

import { useEffect, useRef } from 'react'

import {
  Circle,
  Compass,
  Crosshair,
  EyeOff,
  MessageSquarePlus,
  Square,
  Video,
  X,
} from '@/components/icons'
import { cn } from '@/lib/utils'
import { FEEDBACK_RECORDING_BANNER_TESTID } from '@/lib/feedback/session-recording'
import { FEEDBACK_MODE_EXEMPT_ATTRIBUTE } from './feedback-mode-attributes'
import { HiddenFeedbackThreads } from './hidden-feedback-threads'
import { useFeedbackMode } from './feedback-mode-context'
import { useFeedbackSessionRecording } from './session-recording-context'
import { useFeedbackBannerHeight } from './use-feedback-banner-height'

/** The instruction, verbatim from #13715. */
const INSTRUCTION = 'Select an element to provide targeted feedback'

/** #13788's suspend. The picker is down; the page works normally. */
const BROWSING_INSTRUCTION = 'Picker paused — use the page, or resume selecting'

/**
 * Shared by every control on the bar.
 *
 * `px-3 py-1.5` sizes it on a desktop, as the impersonation bar does. On a
 * touch screen `pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px]`
 * lifts it to 44x44, because these are the controls for a mode a finger can
 * enter (#13640's coarse-pointer path) and for an action with a real
 * start/stop moment. The per-state boundary and tint are appended by the
 * caller.
 */
const CONTROL_BASE =
  'inline-flex items-center justify-center gap-1.5 pointer-coarse:min-h-[44px] ' +
  'pointer-coarse:min-w-[44px] px-3 py-1.5 rounded-md font-medium text-sm transition-colors ' +
  'flex-shrink-0'

/**
 * Feedback-mode chrome: outlined in the amber bar's own foreground.
 *
 * This REPLACES #13756's `BANNER_CONTROL_CLASS`, which was the same shape
 * with `bg-surface-elevated text-text-primary` — the white fill #13757 exists
 * partly to remove, measured at 2.15:1 against the amber. Both of that
 * constant's users (`Done`, and the pins control the same issue added) take
 * this instead, so the pair still reads as a pair.
 */
const MODE_CONTROL = cn(
  CONTROL_BASE,
  'border border-feedback-mode-foreground hover:bg-feedback-mode-control-hover',
)

/** Recording chrome: the same shape, in the red bar's own foreground. */
const RECORDING_CONTROL = cn(
  CONTROL_BASE,
  'border border-feedback-recording-foreground',
  'hover:bg-feedback-recording-control-hover',
)

/**
 * A control's text label, hidden below 400px (#13756, kept by #13788).
 *
 * The same breakpoint and the same reason as `element-picker.tsx`'s
 * `STEP_LABEL_CLASS`: at 320px this banner has 256px of content width and the
 * full labels do not fit on one line beside the mode's own name. The icon and
 * the 44px floor survive; the `aria-label` carries the verb either way, so the
 * control never becomes nameless.
 *
 * `Write feedback` is deliberately NOT given this class. It is the one control
 * a viewer has to be able to read — the same carve-out the picker's CTA had
 * while it lived there.
 */
const NARROW_LABEL_CLASS = 'hidden min-[400px]:inline'

/** `m:ss`. Minutes are unpadded because a leading zero reads as a clock. */
function formatElapsed(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

/**
 * What the live region says. EMPTY while nothing has happened, so a screen
 * reader is not handed a sentence on every route change.
 *
 * Deliberately excludes the elapsed time: a polite live region carrying a
 * clock announces once a second and makes the page unusable.
 */
function announcement(
  /** `status === 'recording'`, NOT "the bar is red" — see the call site. */
  isCapturing: boolean,
  outcome: { kind: string; chunks: number; seconds: number } | null,
): string {
  if (isCapturing)
    return 'Recording started. Text on the page is masked; what you click and where you navigate is recorded.'
  if (!outcome) return ''
  if (outcome.kind === 'start_failed') return 'Recording could not be started.'
  const kept = `Kept ${outcome.chunks} ${
    outcome.chunks === 1 ? 'segment' : 'segments'
  } over ${formatElapsed(outcome.seconds)}.`
  return outcome.kind === 'aborted'
    ? `Recording stopped because uploads kept failing. ${kept}`
    : `Recording stopped. ${kept}`
}

export function FeedbackModeBanner() {
  const {
    mode,
    isActive,
    anchor,
    deactivate,
    requestPlainFeedback,
    beginBrowsing,
    resumePlacing,
  } = useFeedbackMode()
  const {
    isEnabled,
    status,
    elapsedSeconds,
    uploadFailed,
    outcome,
    ensureEnabled,
    start,
    stop,
    cancel,
    dismissOutcome,
  } = useFeedbackSessionRecording()
  const controlsRef = useRef<HTMLDivElement | null>(null)
  const restoreFocusRef = useRef<HTMLElement | null>(null)
  const wasActiveRef = useRef(false)
  const bannerRef = useRef<HTMLDivElement | null>(null)

  /**
   * Ask for the org flag only when a control might actually be offered.
   *
   * #13580's constraint — an org without the flag pays nothing per page load —
   * survives #13757 only because of this line. The banner renders on every
   * route, so an unconditional read here would be exactly the per-request cost
   * the root layout was written to avoid.
   *
   * Since #13841 deleted the user-menu row this is the ONLY caller of
   * `ensureEnabled()` left in the app. The gate is therefore load bearing in a
   * second way: drop the `isActive` term and every route pays; drop the call
   * and the record control below can never render, because `isEnabled` stays
   * `null` forever and the control requires `=== true`.
   *
   * `mode` IS A DEPENDENCY, and it is what makes the failed-read retry
   * reachable (#13841). `ensureEnabled` releases its single-flight latch on a
   * failed read so the next call retries — but with `[isActive, ensureEnabled]`
   * alone, both are stable for as long as the mode is up, so nothing ever
   * MAKES that next call and the retry could only happen by leaving the mode
   * and coming back. Re-asking on the `placing` <-> `browsing` transition gives
   * the recovery a route the viewer already has, inside the session where they
   * noticed the control missing.
   *
   * This does not re-read on every transition: after a read that ANSWERED, the
   * latch stays set and `ensureEnabled()` returns immediately. Only a failure
   * makes a later transition cost a request, which is the single-flight
   * property doing exactly its job.
   */
  useEffect(() => {
    if (isActive) ensureEnabled()
  }, [isActive, mode, ensureEnabled])

  /**
   * Focus the first control when the mode is ENTERED (#13788).
   *
   * This effect used to live in `element-picker.tsx`, where it did two jobs:
   * it saved a keyboard viewer a traversal of the entire page to reach a
   * portal appended to `document.body`, and it was the whole implementation of
   * "`Shift+F` then `Enter` writes feedback" — `button:not([disabled])`
   * resolves to the first enabled control, the CTA was rendered first, and
   * `Enter` activated it natively with no global key handler to double-fire.
   *
   * #13788 moved the CTA here, so the effect had to move with it or the
   * keyboard path would have been silently severed: the picker would still be
   * focusing a bar that no longer holds the button, and `Enter` would do
   * nothing.
   *
   * On ENTRY only, not on every render and not on a `placing` ⇄ `browsing`
   * transition. A viewer who pressed `Browse`, tabbed to a link and pressed
   * `Resume selecting` must not have the caret yanked back to the banner.
   *
   * `restoreFocusRef` is captured on the way in, so leaving returns the caret
   * to whatever the viewer was on rather than to `<body>`.
   */
  useEffect(() => {
    const wasActive = wasActiveRef.current
    wasActiveRef.current = isActive

    if (isActive && !wasActive) {
      restoreFocusRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null
      controlsRef.current
        ?.querySelector<HTMLButtonElement>('button:not([disabled])')
        ?.focus()
      return
    }

    if (!isActive && wasActive) {
      // Only if focus is still inside the banner: a viewer who has clicked
      // away in the meantime should not be yanked back.
      const active = document.activeElement
      const controls = controlsRef.current
      if (
        active === null ||
        active === document.body ||
        (controls !== null && controls.contains(active))
      ) {
        restoreFocusRef.current?.focus?.()
      }
      restoreFocusRef.current = null
    }
  }, [isActive])

  /**
   * Two booleans, not one, and the split is a correctness property.
   *
   * `isRecording` decides the CHROME. It is true during `starting` as well,
   * because `start()` awaits a server action and then a 78 KB rrweb bundle —
   * a real and sometimes slow window — and a press that produced no visible
   * change for a second is the "it just did nothing" this issue is about.
   *
   * `isCapturing` decides every CLAIM. Nothing is recorded until `record()`
   * has returned, so the label, the elapsed clock and the screen-reader
   * announcement all wait for it. A banner that said "Recording this session"
   * over a recorder that then failed to load would have told the person
   * something false about a capture with legal weight, and an announcement is
   * the one form of that which cannot be taken back.
   */
  const isRecording = status === 'recording' || status === 'starting'
  const isCapturing = status === 'recording'
  const isShown = isActive || isRecording || Boolean(outcome)
  useFeedbackBannerHeight(bannerRef, isShown)
  if (!isShown) return null

  /**
   * The title is gated on `isActive` rather than falling through to
   * "Feedback mode", because the render condition above admits a THIRD case
   * neither of the other two labels covers: an `outcome` with the mode off
   * and nothing recording. That is the state a person reaches by pressing
   * "Record session" in the user menu and having the start fail, and an
   * unconditional fallback labelled the resulting banner "Feedback mode" over
   * the words "Could not start recording" — naming a mode the person never
   * entered, on the one surface whose whole job is to say truthfully what is
   * happening. Verified from a rendered capture, not inferred.
   */

  /**
   * The picker stands down while an anchor is pending (`element-picker.tsx`:
   * `const visible = isActive && anchor === null`) because the modal is
   * opening to receive it. The banner does NOT stand down with it — it is the
   * mode's only exit, and an exit that disappears while a modal is up is the
   * failure the `FEEDBACK_MODE_MIN_VIEWPORT` floor used to prevent.
   *
   * But the INSTRUCTION has to go, because during that window there is
   * nothing to select and the sentence is simply false (#13715 review).
   * Dropping the text rather than the banner keeps `Done` reachable and stops
   * the banner telling the viewer to do something the picker is no longer
   * listening for.
   */
  const isPlacing = mode === 'placing' && anchor === null

  return (
    <div
      ref={bannerRef}
      // Issue #13715 — a LITERAL string, never a constant. The golden-path
      // wiring check greps `src/` for the exact spelling the spec asserts, so
      // an id emitted through an identifier scores zero there.
      data-testid="feedback-mode-banner"
      // The state, exposed as data rather than only as a hue. A viewer with a
      // colour-vision deficiency cannot read "red means recording", and a
      // Playwright spec cannot assert it without reading computed styles.
      data-recording={isRecording ? 'true' : undefined}
      {...{ [FEEDBACK_MODE_EXEMPT_ATTRIBUTE]: 'true' }}
      className={cn(
        'sticky top-0 z-feedback-banner shadow-sm',
        isRecording
          ? 'bg-feedback-recording-banner text-feedback-recording-foreground'
          : 'bg-feedback-mode-banner text-feedback-mode-foreground',
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {isRecording ? (
            <Circle
              aria-hidden="true"
              className="w-4 h-4 flex-shrink-0 fill-current"
            />
          ) : (
            <Crosshair className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
          )}
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className="font-medium">
              {isCapturing
                ? 'Recording this session'
                : status === 'starting'
                  ? 'Starting recording…'
                  : isActive
                    ? 'Feedback mode'
                    : 'Session recording'}
            </span>
            {isCapturing && (
              <>
                {/*
                  `tabular-nums` so the row does not reflow once a second as
                  the digits change width, which on a `flex-wrap` row can push
                  a control onto a second line and back.
                */}
                <span
                  data-testid="feedback-recording-elapsed"
                  className="text-sm tabular-nums"
                >
                  {formatElapsed(elapsedSeconds)}
                </span>
                <span className="text-sm flex items-center gap-1">
                  <EyeOff
                    aria-hidden="true"
                    className="w-3 h-3 flex-shrink-0"
                  />
                  {/*
                    What is masked, stated as what it IS rather than as
                    "everything you type". #13580's sentence was written for a
                    recorder that only ran inside feedback mode, where the
                    page was uninteractable and there was nothing to type
                    into; #13757 is what puts it in front of someone using
                    the app for real, which is the moment an overclaim about a
                    capture with legal weight costs something.

                    It is accurate against
                    `FEEDBACK_SESSION_MASK_TEXT_SELECTOR` (`'*'`), and it says
                    the second half out loud: masking text does not make a
                    session recording private, because the route you visited
                    and the control you pressed are the recording.
                  */}
                  Text is masked; your clicks and navigation are recorded
                </span>
              </>
            )}
            {/*
              NOT `truncate`. At 320px the instruction is the longer of the
              two strings, and truncating it renders "Select an element to
              pr…" — which loses the verb the whole banner exists to supply.
              The row already wraps, and an in-flow banner growing a line is
              exactly what an in-flow banner is for.
            */}
            {!isRecording && isPlacing && (
              <span className="text-sm">{INSTRUCTION}</span>
            )}
            {/*
              `browsing` needs its own sentence, not the absence of one
              (#13788). The banner is still up and the mode is still on, so a
              viewer who sees amber and no explanation is left guessing why;
              and the placing instruction would be simply false there, because
              nothing is listening for a click.
            */}
            {!isRecording && mode === 'browsing' && (
              <span className="text-sm">{BROWSING_INSTRUCTION}</span>
            )}
            {isRecording && uploadFailed && (
              /*
                AC 8. The uploader RETAINS a failed chunk's events and retries
                the same index, so from the inside a broken upload path looks
                exactly like a healthy one — which is how a recording
                degrades to nothing without anybody being told. `role="alert"`
                rather than the polite region below: this is the one thing on
                the bar the person may want to act on (stop, check the
                network) while it is still true.
              */
              <span
                data-testid="feedback-recording-upload-error"
                role="alert"
                className="text-sm font-medium"
              >
                Upload failing — retrying
              </span>
            )}
            {!isRecording && outcome && (
              <span
                data-testid="feedback-recording-outcome"
                className="text-sm"
              >
                {outcome.kind === 'start_failed'
                  ? 'Could not start recording'
                  : announcement(false, outcome)}
              </span>
            )}
          </div>
        </div>
        {/*
          THE CONTROL ROWS (#13788). Three of them, and which one renders is
          the state:

            placing   — Write feedback · Browse · [hidden] · record · exit
            browsing  — Resume selecting · [hidden] · record · exit
            recording — Stop recording · Cancel

          The `record` control is the ONLY entry point into a session
          recording since #13841 deleted the user-menu row, so it is also the
          only caller of `ensureEnabled()` — see the effect above.

          `Write feedback` is FIRST, and that placement is the whole `Enter`
          implementation: the focus effect above selects the first enabled
          control on entry. Reordering these silently breaks `Shift+F` →
          `Enter`.

          The pins control that #13756 put here is GONE and did not survive
          its stay in the user menu either: #13841 deleted it with the ambient
          layer that made it necessary.
        */}
        <div
          ref={controlsRef}
          className="flex items-center gap-2 flex-shrink-0"
        >
          {isRecording ? (
            <>
              {/*
                `Stop recording` LANDS somewhere now (#13788). It used to
                leave an outcome string in the banner and nothing to do with
                it — and `feedback-modal.tsx` read the active session id at
                SUBMIT time, which `stop` had already nulled, so a modal
                opened afterwards attached nothing at all. The recording is
                handed to the pending-attachment slot and the feedback modal
                opens with it shown as an attachment.

                Only while `recording`: during `starting` there is nothing to
                keep, so the only honest control is Cancel.
              */}
              {isCapturing && (
                <button
                  type="button"
                  data-testid="feedback-recording-stop"
                  onClick={() => {
                    stop()
                    // Opening the modal is the banner's job rather than the
                    // recording context's: that module has no
                    // `useFeedbackMode()` call and must not gain one — a
                    // dependency in that direction is what re-created the
                    // "recording only exists inside feedback mode" trap
                    // #13757 was filed about.
                    requestPlainFeedback()
                  }}
                  aria-label="Stop recording: end this recording and write feedback about it"
                  className={RECORDING_CONTROL}
                >
                  <Square className="w-4 h-4 fill-current" aria-hidden="true" />
                  <span>Stop recording</span>
                </button>
              )}
              {/*
                ENABLED during `starting`, and that is load bearing.
                `cancelledRef` in `session-recording-context.tsx` exists
                precisely for a cancel pressed while `start` is still awaiting
                the session action or the 78 KB rrweb bundle; disabling the
                only exit for exactly that window made both of its branches
                unreachable and left a stranded banner when a start failed.
              */}
              <button
                type="button"
                data-testid="feedback-recording-cancel"
                onClick={cancel}
                aria-label={
                  status === 'starting'
                    ? 'Cancel: stop starting this recording and discard it'
                    : 'Cancel: discard this recording'
                }
                className={RECORDING_CONTROL}
              >
                <X className="w-4 h-4" aria-hidden="true" />
                <span>Cancel</span>
              </button>
            </>
          ) : (
            isActive && (
              <>
                {mode === 'placing' && (
                  /*
                    The CTA, moved up from the picker's floating bar (#13788).
                    On a fine pointer that bar held exactly one button, which
                    is two amber chromes for one mode; and while an anchor was
                    pending the bar stood down entirely, so the CTA vanished
                    exactly when the viewer might want it.

                    It opens the ORDINARY feedback form. The anchored path is
                    the click itself: on a fine pointer a click commits, and on
                    the coarse two-step path a second tap on the highlighted
                    element does. A viewer who wants to say something about no
                    particular element must never be stuck in a mode that
                    insists they point at one, and this is that escape hatch.
                  */
                  <button
                    type="button"
                    data-testid="feedback-mode-banner-write"
                    onClick={requestPlainFeedback}
                    aria-label="Write feedback: send feedback without selecting an element"
                    className={MODE_CONTROL}
                  >
                    <MessageSquarePlus className="w-4 h-4" aria-hidden="true" />
                    <span>Write feedback</span>
                  </button>
                )}
                {/*
                  A LABEL-CHANGING toggle and no `aria-pressed`, which is the
                  pairing the APG specifies and the one the pins control used:
                  a toggle whose label already reports the state must not also
                  carry `aria-pressed`, or the two are announced together
                  ("Resume selecting, pressed") and contradict each other for
                  the one population that cannot see which it is.

                  Its job is exactly one thing. With reading and replying now
                  working while armed, navigation is the only thing the capture
                  layer genuinely blocks.
                */}
                <button
                  type="button"
                  data-testid="feedback-mode-banner-browse"
                  onClick={mode === 'placing' ? beginBrowsing : resumePlacing}
                  aria-label={
                    mode === 'placing'
                      ? 'Browse: put the element picker down so you can use the page'
                      : 'Resume selecting: pick up the element picker again'
                  }
                  className={MODE_CONTROL}
                >
                  {mode === 'placing' ? (
                    <Compass className="w-4 h-4" aria-hidden="true" />
                  ) : (
                    /* `Crosshair`, not #13788's `Target` (#13841). The
                       banner's own leading icon in this state is the
                       crosshair above, and that is what a viewer reads as
                       "you are in the element picker" — so the control that
                       picks it back up has to be the same tool, not a second
                       glyph for one idea. `Compass` stays on the Browse half:
                       that one means something different. */
                    <Crosshair className="w-4 h-4" aria-hidden="true" />
                  )}
                  <span className={NARROW_LABEL_CLASS}>
                    {mode === 'placing' ? 'Browse' : 'Resume selecting'}
                  </span>
                </button>
                {/*
                  #13653 — threads anchored inside a collapsed section draw
                  no pin. Renders only when there are some; the decision
                  record is in `pin-overlay-mount.tsx`.
                */}
                <HiddenFeedbackThreads
                  className={MODE_CONTROL}
                  labelClassName={NARROW_LABEL_CLASS}
                />
                {isEnabled === true && (
                  /*
                    ICON-ONLY, and `Video` rather than #13757's filled
                    `Circle`. A filled dot reads as a bullet or a status light;
                    a camcorder reads as "this records". The verb is in the
                    accessible name, which is the only name this control has.

                    Starting a recording drops the mode to `off`: the capture
                    layer has to come down, or the person recording themselves
                    using the app cannot use the app.
                  */
                  <button
                    type="button"
                    data-testid="feedback-recording-start"
                    onClick={() => {
                      deactivate()
                      start()
                    }}
                    aria-label="Start recording this session"
                    className={MODE_CONTROL}
                  >
                    <Video className="w-4 h-4" aria-hidden="true" />
                  </button>
                )}
                {/*
                  ICON-ONLY since #13788 — the row got two more controls and
                  "Done" was the cheapest word to spend. The accessible name
                  still leads with a verb for WCAG 2.5.3, and 44x44 holds
                  because this is the ONLY exit from a mode a finger can enter.
                */}
                <button
                  type="button"
                  data-testid="feedback-mode-banner-done"
                  onClick={deactivate}
                  aria-label="Exit feedback mode"
                  className={MODE_CONTROL}
                >
                  <X className="w-4 h-4" aria-hidden="true" />
                </button>
              </>
            )
          )}
          {!isRecording && outcome && (
            <button
              type="button"
              data-testid="feedback-recording-outcome-dismiss"
              onClick={dismissOutcome}
              aria-label="Dismiss the recording confirmation"
              className={MODE_CONTROL}
            >
              <X className="w-4 h-4" aria-hidden="true" />
              <span>Dismiss</span>
            </button>
          )}
        </div>
      </div>

      {/*
        #13580's "you are being recorded" region, kept under its own testid
        and reduced to what it is actually good at: the ANNOUNCEMENT. The
        visible state is the bar itself, so this is `sr-only` — and
        `aria-atomic` matters because without it a screen reader reads only
        the words that changed.
      */}
      <div
        id={FEEDBACK_RECORDING_BANNER_TESTID}
        data-testid={FEEDBACK_RECORDING_BANNER_TESTID}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement(isCapturing, outcome)}
      </div>
    </div>
  )
}
